import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { readTokens, getTimeToExpiry, isExpired } from "./store";
import { getValidAccessToken, login, logout, refreshTokensIfNeeded, forceRefreshTokens } from "./client";
import { decodeJwtPayload } from "../lib/jwt";
import type { AuthUser, AuthContextValue, AuthCtx } from "./types";

// Context with null default (enforced by useAuth)
const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);

  /**
   * Refresh tokens and update state
   */
  const refreshAuth = useCallback(async (): Promise<string | null> => {
    try {
      const token = await getValidAccessToken();
      setAccessToken(token);

      const tokens = readTokens();
      if (tokens?.id_token) {
        setIdToken(tokens.id_token);
        setUser(decodeJwtPayload<AuthUser>(tokens.id_token));
      } else {
        setIdToken(null);
        setUser(null);
      }

      return token;
    } catch (error) {
      console.error("[AuthProvider] Failed to refresh auth:", error);
      setAccessToken(null);
      setIdToken(null);
      setUser(null);
      return null;
    }
  }, []);

  /**
   * Get valid access token (for HTTP API with JWT authorizer)
   * Refreshes if needed
   */
  const ensureToken = useCallback(async (): Promise<string | null> => {
    const token = await refreshAuth();
    return token;
  }, [refreshAuth]);

  /**
   * Get ID token (for REST API with Cognito User Pools authorizer)
   * Checks expiry and triggers a refresh if the token is expired.
   */
  const ensureIdToken = useCallback(async (): Promise<string | null> => {
    const tokens = readTokens();
    if (!tokens?.id_token) return null;

    // If tokens are expired, refresh them first
    if (isExpired(tokens)) {
      const refreshed = await refreshAuth();
      if (!refreshed) return null;
      const freshTokens = readTokens();
      return freshTokens?.id_token ?? null;
    }

    // Update user state if not already set
    if (!user) {
      setUser(decodeJwtPayload<AuthUser>(tokens.id_token));
    }
    setIdToken(tokens.id_token);
    return tokens.id_token;
  }, [user, refreshAuth]);

  /**
   * Alias for ensureToken (for API client compatibility)
   */
  const getToken = useCallback(async (): Promise<string | null> => {
    return ensureToken();
  }, [ensureToken]);

  /**
   * Force a token refresh regardless of local expiry (called on 401).
   * Updates React state so subsequent renders have the fresh token.
   */
  const forceRefresh = useCallback(async (): Promise<string | null> => {
    try {
      const tokens = await forceRefreshTokens();
      if (tokens) {
        setAccessToken(tokens.access_token);
        if (tokens.id_token) {
          setIdToken(tokens.id_token);
          setUser(decodeJwtPayload<AuthUser>(tokens.id_token));
        }
        return tokens.access_token;
      }
      return null;
    } catch (error) {
      console.error("[AuthProvider] Force refresh failed:", error);
      return null;
    }
  }, []);

  // Initialize auth on mount
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      await refreshAuth();
      if (mounted) {
        setIsLoading(false);
      }
    };

    void init();

    return () => {
      mounted = false;
    };
  }, [refreshAuth]);

  // Background token refresh disabled — getToken reads from localStorage
  // via ensureToken() which refreshes on-demand when the token is expired.

  // Build context value
  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: !!accessToken,
      isAuthed: !!accessToken, // Backward compatibility alias
      isLoading,
      accessToken,
      idToken,
      user,
      login,
      logout,
      ensureToken,
      ensureIdToken,
      getToken,
      forceRefresh,
    }),
    [accessToken, idToken, user, isLoading, ensureToken, ensureIdToken, getToken, forceRefresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access auth context
 * @throws Error if used outside of AuthProvider
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}

/**
 * Legacy hook that returns the old AuthCtx shape
 * @deprecated Use useAuth() instead
 */
export function useAuthLegacy(): AuthCtx {
  const auth = useAuth();
  return useMemo(
    () => ({
      isAuthed: auth.isAuthenticated,
      accessToken: auth.accessToken,
      user: auth.user,
      login: auth.login,
      logout: auth.logout,
      ensureToken: auth.ensureToken,
      ensureIdToken: auth.ensureIdToken,
    }),
    [auth]
  );
}
